import { ReactElement, useState } from 'react';

import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { Button, Modal } from '@/shared/design-system';

import {
  ActionInfoCard,
  ActionModalActions,
  ActionModalBody,
  ActionModalSeparator,
} from './action-modal-shared';

export type ConfirmActionModalProps = {
  modalTitle: string;
  btnLabel: string;
  icon: ReactElement;
  confirmationTitle: string;
  confirmationDescription?: string;
  onConfirm: () => void | Promise<void>;
  btnColor: 'red' | 'green' | 'orange';
  isLoading?: boolean;
};

const buttonColorByBtnColor = (color: ConfirmActionModalProps['btnColor']) => {
  if (color === 'red') return 'destructive' as const;
  if (color === 'green') return 'primary' as const;
  return 'warning' as const;
};

export const ConfirmActionModal = () => {
  const { open, handleClose, props } = useGlobalModal('CONFIRM_ACTION_MODAL');
  const [isPending, setIsPending] = useState(false);

  const handleConfirm = async () => {
    setIsPending(true);
    try {
      await props?.onConfirm();
    } finally {
      setIsPending(false);
    }
  };

  const isLoading = isPending || props?.isLoading;
  const buttonColor = buttonColorByBtnColor(props?.btnColor ?? 'red');

  return (
    <Modal
      open={open}
      onOpenChange={(o) => (!o ? handleClose() : undefined)}
      title={props?.modalTitle ?? ''}
      showClose
    >
      <ActionModalBody>
        <ActionModalSeparator />
        <ActionInfoCard
          icon={props?.icon}
          title={props?.confirmationTitle ?? ''}
          description={props?.confirmationDescription}
        />
        <ActionModalSeparator />
        <ActionModalActions>
          <Button
            size="lg"
            fullWidth
            disabled={isLoading}
            onClick={handleConfirm}
            color={buttonColor}
            variant="solid"
          >
            {props?.btnLabel ?? 'Confirm'}
          </Button>
          <Button color="tertiary" variant="outline" size="lg" fullWidth onClick={handleClose}>
            Cancel
          </Button>
        </ActionModalActions>
      </ActionModalBody>
    </Modal>
  );
};
